import { useMutation } from '@apollo/client'
import { Button, CircularProgress, Paper, Typography } from '@material-ui/core'
import React from 'react'
import { toast } from 'react-toastify'

import VisuallyHidden from '../../components/VisuallyHidden'
import { BULK_ASSIGN_TICKETS_MUTATION } from '../../graphql/tickets'
import {
  summarizeBulkAssign,
  Ticket,
  TicketBulkAssignResult,
  TicketBulkAssignTarget
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import useStyles from './styles/BulkAssignBar.styles'

interface BulkAssignBarProps {
  /** The SELECTED tickets, not their ids — `version` has to travel with each. */
  selected: Ticket[]
  onClear: () => void
  onDone: () => void
  /** Lets the list freeze its checkboxes while a batch is in flight. */
  onBusyChange?: (busy: boolean) => void
}

/**
 * Acts on a selection, and reports what actually happened to it.
 *
 * Every button here maps to one server-side call that loops per ticket. The
 * loop is NOT here: the panel's session carries no user id, so "assign to me"
 * cannot be expressed client-side at all, and a client-supplied id would be an
 * authorization decision made by the caller.
 *
 * Partial success is the normal outcome, not an error. A queue mutates under
 * concurrent agents, so a row going stale between the list loading and the
 * button being pressed is ordinary — the summary says how many landed and why
 * the rest did not, rather than showing a red toast that implies nothing did.
 */
export default function BulkAssignBar({
  selected,
  onClear,
  onDone,
  onBusyChange
}: BulkAssignBarProps) {
  const classes = useStyles()
  const [announcement, setAnnouncement] = React.useState('')
  // The batch outlives the component if the operator navigates away mid-flight.
  // Without this the resolved promise sets state on an unmounted tree and calls
  // refetch on a list nobody is looking at, racing whatever query the new screen
  // just issued.
  const mounted = React.useRef(true)
  React.useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )
  const [run, { loading }] = useMutation<{
    bulkAssignTickets: TicketBulkAssignResult[]
  }>(BULK_ASSIGN_TICKETS_MUTATION)

  // Nothing selected is as good a reason to refuse as a request in flight, now
  // that the bar stays mounted through both.
  const busy = loading || !selected.length

  React.useEffect(() => {
    onBusyChange?.(loading)
  }, [loading, onBusyChange])

  const submit = async (target: TicketBulkAssignTarget) => {
    try {
      const response = await run({
        variables: {
          items: selected.map((ticket) => ({
            id: ticket.id,
            // From the loaded ticket, never re-fetched or defaulted: a wrong
            // version is exactly what optimistic concurrency exists to reject.
            version: ticket.version
          })),
          target
        }
      })
      const results = response.data?.bulkAssignTickets || []
      const { assigned, failed, reasons, unexplained } =
        summarizeBulkAssign(results)

      // Counts AND causes. "2 failed" leaves an operator with nothing to do;
      // a cause without a count hides how much of the batch landed.
      // ONE key carrying both clauses, not two sentences joined by a space.
      // A translator cannot reorder, re-punctuate or merge clauses assembled in
      // JS — the sibling list in this same screen states that rule for exactly
      // this reason, and this violated it.
      let message = getTranslate('{assigned} of {total} assigned.', {
        assigned,
        total: results.length
      })
      if (failed > 0) {
        const known = reasons.map((reason) => getTranslate(reason))
        // Named separately rather than folded into the known causes: a reason
        // list that covers only some of the failures, presented as if it
        // covered all of them, tells the operator to retry when part of the
        // batch has a different problem.
        if (unexplained > 0) {
          known.push(
            known.length
              ? getTranslate('{count} for another reason', {
                  count: unexplained
                })
              : getTranslate('the server rejected them')
          )
        }
        message = getTranslate(
          '{assigned} of {total} assigned. {failed} not assigned — {why}.',
          {
            assigned,
            total: results.length,
            failed,
            // A translated separator: Persian's list separator is ، (U+060C),
            // and a hardcoded ASCII ", " is a JS literal no translator can
            // reach.
            why: known.join(getTranslate('LIST_SEPARATOR'))
          }
        )
      }

      // Not an error toast when anything landed: a red banner over a batch that
      // mostly worked tells the operator to redo work already done.
      if (!mounted.current) return
      if (failed && !assigned) toast.error(message)
      else toast.success(message)
      setAnnouncement(message)
      onClear()
      onDone()
    } catch (err) {
      if (!mounted.current) return
      const message = getTranslate('Could not assign the selected tickets.')
      toast.error(message)
      setAnnouncement(message)
    }
  }

  return (
    <Paper
      className={classes.bar}
      role="region"
      aria-label={getTranslate('Bulk actions')}>
      {/* A live status, matching the filter row three hundred lines away in
          this same screen: without it a screen-reader user ticking twelve
          boxes never hears the count change. */}
      <Typography
        variant="body2"
        className={classes.count}
        role="status"
        aria-live="polite"
        aria-atomic="true">
        {getTranslate('{count} selected', { count: selected.length })}
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="primary"
        disabled={busy}
        onClick={() => submit('CALLER')}>
        {getTranslate('Assign to me')}
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={() => submit('ADOTEL_ADMIN')}>
        {getTranslate('Escalate to Adotel')}
      </Button>
      <Button size="small" disabled={busy} onClick={onClear}>
        {getTranslate('Clear selection')}
      </Button>
      {loading && (
        <CircularProgress size={18} aria-label={getTranslate('Assigning')} />
      )}
      {/* The outcome is a toast, which screen readers do not reliably announce
          and which vanishes. The live region is the durable half. */}
      <VisuallyHidden live="polite">{announcement}</VisuallyHidden>
    </Paper>
  )
}
